/* eslint-disable prefer-destructuring */
/* eslint-disable @typescript-eslint/ban-ts-comment */

import { useEffect, useMemo, useState } from 'react';

import _ from 'lodash';
import { ChartProps } from 'react-chartjs-2';

import { ChartConfig } from 'components/organisms/InteractiveChart';

type ChartData = ChartProps['data'];

function isValidHttpUrl(string: string): boolean {
    let url;
    try {
        url = new URL(string);
    } catch (e) {
        return false;
    }
    return url.protocol === 'http:' || url.protocol === 'https:';
}

const loadScript = (id: string, url: string): Promise<void> => {
    return new Promise((resolve) => {
        if (document.getElementById(id) === null) {
            const script = document.createElement('script');
            script.setAttribute('src', url);
            script.setAttribute('id', id);
            document.body.appendChild(script);

            script.onload = () => {
                setTimeout(() => {
                    resolve();
                }, 500);
            };
        } else {
            resolve();
        }
    });
};

const transformGroup = (source: ChartData): ChartData => {
    const data = _.cloneDeep(source) as ChartData;
    const { datasets, labels = [] } = data;

    if (datasets[0]) {
        const groups = datasets[0].data;

        labels.forEach((label, i) => {
            if (label) {
                labels[i] = `${label} ${groups[i]}`;
            } else {
                labels[i] = groups[i];
            }
        });

        delete data.datasets[0];

        const tempDataset: ChartData['datasets'] = [];

        data.datasets.forEach((item, i) => {
            if (item) tempDataset.push(data.datasets[i]);
        });
        data.datasets = tempDataset;
    }

    return data;
};

export const useParseChart = (
    options?: ChartConfig
): {
    data: ChartData;
    loading: boolean;
} => {
    const { file, parsingOption = 'index' } = options || {};

    const [scriptsLoaded, setScriptsLoaded] = useState(false);

    const [originalChartData, setOriginalChartData] = useState<ChartData>({
        datasets: [],
    });

    const [chartData, setChartData] = useState<ChartData>({
        datasets: [],
    });

    const [loading, setIsLoading] = useState(true);

    useEffect(() => {
        setScriptsLoaded(false);

        loadScript('chart.js', 'https://cdn.jsdelivr.net/npm/chart.js@2.8.0')
            .then(() => {
                return loadScript(
                    'xlsx',
                    'https://cdn.sheetjs.com/xlsx-0.19.0/package/dist/xlsx.full.min.js'
                ).then(() => {
                    return loadScript(
                        'datasource',
                        'https://cdn.jsdelivr.net/npm/chartjs-plugin-datasource@0.1.0'
                    );
                });
            })
            .then(() => {
                setTimeout(() => {
                    setScriptsLoaded(true);
                }, 500);
            });
    }, []);

    useEffect(() => {
        const ctx = document.createElement('canvas');

        if (scriptsLoaded && file && isValidHttpUrl(file)) {
            setIsLoading(true);

            // @ts-ignore:next-line
            const Chart = window.Chart;
            // @ts-ignore:next-line
            const ChartDataSource = window.ChartDataSource;

            const fileUrl = new URL(file);

            const chartGraph = new Chart(ctx, {
                type: 'bar',
                plugins: [ChartDataSource],
                options: {
                    plugins: {
                        datasource: {
                            url: `${fileUrl.pathname.replace(
                                '/static-dev',
                                ''
                            )}${fileUrl.search}`,
                            rowMapping: parsingOption,
                        },
                    },
                },
            });

            setTimeout(() => {
                setOriginalChartData(chartGraph.data);
                setIsLoading(false);
                ctx.remove();
            }, 2000);
        }
    }, [file, parsingOption, scriptsLoaded]);

    const coloredChartData = useMemo<ChartData>(() => {
        return {
            ...originalChartData,
            datasets: originalChartData.datasets.map((ds) => {
                const dsColor = options?.chartColors?.find(
                    (c) => c.label === ds.label
                );

                return {
                    ...ds,
                    backgroundColor: dsColor?.backgroundColor,
                    borderColor: dsColor?.borderColor,
                };
            }),
        };
    }, [options?.chartColors, originalChartData]);

    useEffect(() => {
        if (options?.group) {
            setChartData(transformGroup(coloredChartData));
        } else {
            setChartData(coloredChartData);
        }
    }, [coloredChartData, options?.group]);

    return {
        data: chartData,
        loading: loading || !scriptsLoaded,
    };
};
